<template>
    <div :class="['no-print', 'header-fixed-top', navCollapsed ? 'long-block' : '']">
        <div class="up">
            <h1 class="title">{{ title }}</h1>
            <div class="header-buttons">
                <button
                    v-for="(button, index) in headerButtons"
                    :key="index"
                    :disabled="button.isDisabled()"
                    type="button"
                    :class="[button.class, 'btn']"
                    @click.prevent="button.action"
                >
                    <i :class="button.icon"></i> {{ $t(button.text) }}
                </button>
            </div>
        </div>
        <div class="low">
            <ul v-if="tabs.length > 0" :class="['nav-tabs', { 'col-xs-8': header.isSearchEnabled }]">
                <li v-show="leftDotsShow" class="nav-tabs__dots" @click="scrollTo(true)">
                    <i class="icon-three-dots"></i>
                </li>
                <div ref="visibleArea" class="nav-tabs__scroll">
                    <div ref="allLinks">
                        <RouterLink
                            v-for="tab in tabs"
                            :key="tab.name"
                            tag="li"
                            :to="{ name: tab.router, params: tab.params ? tab.params : {} }"
                        >
                            <a @click="clearMessages">{{ $t(tab.name) }}</a>
                        </RouterLink>
                    </div>
                </div>
                <li v-show="rightDotsShow" class="nav-tabs__dots" @click="scrollTo(false)">
                    <i class="icon-three-dots"></i>
                </li>
            </ul>
            <div
                v-show="header.isSearchEnabled"
                :class="['search', 'col-xs-4', header.keyword !== '' ? 'active-search' : '']"
            >
                <label v-if="!isLoading" for="forAT_Layout-search" class="label-for-screen-reader">
                    {{ $t('common.searchPlaceholder') }}
                </label>
                <input
                    v-if="!isLoading"
                    id="forAT_Layout-search"
                    type="text"
                    maxlength="30"
                    autocomplete="off"
                    :disabled="header.isSearchBlocked"
                    :value="header.keyword"
                    class="search-input"
                    @input="debounceSetKeyword($event.target.value)"
                />
                <i v-if="!isLoading" class="icon-search"></i>
                <button v-if="!isLoading" :disabled="header.isSearchBlocked" @click="setKeyword('')">
                    <i class="icon-close"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import { routes } from '../../router/routerMap';
import { mapState, mapMutations, mapActions } from 'vuex';
import { COMPONENT_NAMES, DEBOUNCE_INTERVAL, DEBOUNCE_SEARCH } from '../../constants';
import debounce from 'lodash-es/debounce';
import isEqual from 'lodash-es/isEqual';

export default {
    name: COMPONENT_NAMES.HEADER_VIEW,

    data() {
        return {
            fullLinksWidth: 0,
            visibleLinksWidth: 0,
            scrollLeft: 0,
            // this function must be marked for removing in handler listener before destroy component
            debounceResizeVisibleLinksWidth: debounce(
                () => this.resizeVisibleLinksWidth(),
                DEBOUNCE_INTERVAL
            ),
            debounceSetKeyword: debounce(searchText => this.setSearchText(searchText), DEBOUNCE_SEARCH),
        };
    },

    computed: {
        ...mapState({
            header: state => state.header,
            isLoading: state => state.isLoading,
            navCollapsed: state => state.navCollapsed,
            authorized: state => state.user.authorized,
        }),

        headerButtons() {
            return this.header.buttons.filter(button => button.text && button.action);
        },

        /**
         * Returns page title
         * @returns {String}
         */
        title() {
            let title = this.header.title ? this.$t(this.header.title) : this.$t(this.header.externalTitle);
            document.title = title.length > 0 ? 'MARi - ' + title : 'MARi';
            return title;
        },

        /**
         * Returns array of objects with two keys: router (name) and (tab) name
         * @returns {Array}
         */
        tabs() {
            let routeName = this.$route.name;
            if (routeName) {
                const topRouteName = routeName.split('.')[0];
                let route = routes.find(route => route.name === topRouteName);

                if (route && route.meta && route.meta.autoTabs) {
                    return route.children.map(({ name }) => ({ router: name, name: name + 'Tab.title' }));
                }
            }

            return [];
        },

        leftDotsShow() {
            return this.scrollLeft > 0;
        },

        rightDotsShow() {
            return this.visibleLinksWidth + this.scrollLeft < this.fullLinksWidth;
        },
    },

    watch: {
        navCollapsed() {
            setTimeout(() => {
                this.resizeVisibleLinksWidth();
            }, 300);
        },

        isLoading() {
            this.resizeVisibleLinksWidth();
        },

        tabs(prev, next) {
            if (!isEqual(prev, next)) {
                this.$nextTick(() => {
                    this.resizeVisibleLinksWidth();
                });

                this.scrollLeft = 0;
                this.fullLinksWidth = 0;
                this.visibleLinksWidth = 0;
            }
        },
    },

    created() {
        window.addEventListener('resize', this.debounceResizeVisibleLinksWidth);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.debounceResizeVisibleLinksWidth);
    },

    methods: {
        ...mapActions({
            clearMessages: 'popup/clearMessages',
        }),

        ...mapMutations({
            setKeyword: 'header/SET_KEYWORD',
        }),

        setSearchText(value) {
            this.setKeyword(value);
        },

        resizeVisibleLinksWidth() {
            if (this.$refs.visibleArea && this.$refs.allLinks) {
                this.visibleLinksWidth = this.$refs.visibleArea.clientWidth;
                this.fullLinksWidth = this.$refs.allLinks.clientWidth;

                if (this.visibleLinksWidth + this.scrollLeft > this.fullLinksWidth) {
                    this.scrollLeft = this.fullLinksWidth - this.visibleLinksWidth;
                }
            }
        },

        scrollTo(toLeft = true) {
            let newScrollValue = Math.min(
                this.scrollLeft + (toLeft ? -this.visibleLinksWidth : this.visibleLinksWidth),
                this.fullLinksWidth - this.visibleLinksWidth
            );

            this.scrollLeft = Math.max(newScrollValue, 0);
            this.$refs.visibleArea.scrollLeft = this.scrollLeft;
        },
    },
};
</script>
